import React, { ReactNode, useEffect, useState } from "react";

import { head } from "@utils/array";
import { shouldDisableAddToCart } from "@utils/disableAddToCart";
import { StockLevels } from "@utils/enums";
import { isDefined } from "@utils/helpers";

import { StoreDetailsModel } from "@redux/modules/api/data/stores/@types/storeDetails.model";
import { APIStatus } from "@redux/modules/api/data/stores/storeLocator/storeLocator.module";
import { LocalizedStoresSearchParams } from "@redux/modules/api/data/stores/storeLocator/storeLocator.types";
import { clearMessages } from "@redux/modules/value/messages/messages.utils";
import { useGranite } from "@redux/modules/value/translations/translations.provider";

import ErrorMessage from "@components/ErrorMessages/errorMessages.component";
import { Button } from "@components/General/Button/button.component";

import { MAX_STORES, STORE_INCREMENT } from "./settings";
import StoreList from "./storeList/storeList.component";
import "./storePicker.scss";
import StoreSearch from "./storeSearch/storeSearch.component";

export type StorePickerProps = {
    stores?: Array<StoreDetailsModel>;
    loading: boolean;
    error: string;
    onSearch: (params: LocalizedStoresSearchParams) => Promise<any>;
    onSubmit: (store: StoreDetailsModel) => void;
    infoLink?: string;
    buttonTextTK?: string;
    storeCode?: string;
    storesList: Array<StoreDetailsModel>;
    storeFilter?: (
        store: StoreDetailsModel,
        params?: LocalizedStoresSearchParams,
    ) => boolean;
    isOnlyAvailableInStore: boolean;
    displayStockLevel?: boolean;
    customSubmit?: (onClick: () => void) => ReactNode;
    disabledGeoLocation?: boolean;
};

const StorePicker = ({
    storeCode,
    onSearch,
    // stores = result list of stores
    stores,
    // storeList = full list for autocomplete
    storesList,
    storeFilter,
    customSubmit,
    onSubmit,
    error,
    loading,
    infoLink,
    buttonTextTK,
    isOnlyAvailableInStore,
    displayStockLevel = false,
    disabledGeoLocation,
}: StorePickerProps) => {
    const { t } = useGranite();
    const [geoLoading, setGeoloading] = useState(false);
    const [amount, setAmount] = useState(3);
    const [store, setStore] = useState<any>(null);
    const [params, setParams] = useState<
        LocalizedStoresSearchParams | undefined
    >(undefined);
    const [searchTriggered, setSearchTriggered] = useState(false);
    const isLoading = loading || geoLoading;
    const buttonTk = buttonTextTK
        ? buttonTextTK
        : "general.storepicker.button.confirmStore";

    useEffect(() => {
        clearMessages();

        if (storeCode) {
            onSearch({
                limit: MAX_STORES,
                storeId: storeCode,
                filterEmptyStock: false,
                includeProductAvailability: displayStockLevel,
            });
        }

        return function onCleanUp() {
            clearMessages();
            setSearchTriggered(false);
        };
    }, []);

    useEffect(() => {
        if (stores) {
            if (storeCode && !store && !searchTriggered) {
                setStoreWithStoreCode(stores, storeCode);
                return;
            }
            if (!store) setFirstStore(stores);
        }
    });

    const setStoreWithStoreCode = (
        stores: Array<StoreDetailsModel>,
        storeCode: string,
    ) => {
        const store = stores.find(({ code }) => code === storeCode);
        handleSelect(store);
    };

    const setFirstStore = (stores: Array<StoreDetailsModel>) => {
        if (!displayStockLevel) {
            handleSelect(head(stores));
            return;
        }
        const firstStoreInStock = stores.find(
            ({ productAvailability }) =>
                productAvailability?.stockLevel &&
                ~[StockLevels.InStock, StockLevels.LowStock].indexOf(
                    productAvailability.stockLevel,
                ),
        );
        handleSelect(firstStoreInStock);
    };

    const handleSelect = (shop?: StoreDetailsModel) => {
        setStore(shop);
    };

    const hasStock = () => {
        if (!displayStockLevel) {
            return true;
        }

        let hasStock = false;

        if (store) {
            const stockLevel = store.productAvailability.stockLevel;

            hasStock =
                stockLevel !== StockLevels.NoStock &&
                stockLevel !== StockLevels.UnknownStock;
        }

        return hasStock;
    };

    const onClick = () => {
        if (store) onSubmit(store);
    };

    const isDisabled = () => {
        if (loading || store) {
            return false;
        }

        if (shouldDisableAddToCart(store?.productAvailability)) {
            return true;
        }

        return !hasStock();
    };

    let errorMsg = t(`general.storepicker.${error}`);
    if (!errorMsg && error) {
        errorMsg = t("general.storepicker.fetchStoreFailed");
    }

    return (
        <div className="storePicker-layout store-picker-context">
            {error && (
                <div className="mt-2">
                    <ErrorMessage message={errorMsg} />
                </div>
            )}
            <div className="storePicker-layout--header">
                <p>{t("storepicker.postalcode.helper")}</p>
                <StoreSearch
                    storesList={storesList.filter(s =>
                        storeFilter ? storeFilter(s) : true,
                    )}
                    disabled={isLoading}
                    onSearch={params => {
                        setSearchTriggered(true);
                        setParams(params);
                        onSearch(params).then(({ type }) => {
                            if (type === APIStatus.SUCCESS) {
                                setStore(null);
                            }
                        });
                    }}
                    onChangeAmount={newAmount => setAmount(newAmount)}
                    onGeoLoading={newState => setGeoloading(newState)}
                    withStock={displayStockLevel}
                    disabledGeoLocation={disabledGeoLocation}
                    storeName={store?.storeName}
                />
            </div>
            <div className="storePicker-layout--content">
                <StoreList
                    loading={isLoading}
                    error={error}
                    stores={(stores || []).filter(s =>
                        storeFilter ? storeFilter(s, params) : true,
                    )}
                    storesLoaded={isDefined(stores)}
                    amount={amount}
                    shopCode={store && store.code}
                    onSelect={store => handleSelect(store)}
                    onShowMore={() => {
                        if (
                            amount < MAX_STORES &&
                            stores &&
                            stores.length > amount
                        )
                            setAmount(amount + STORE_INCREMENT);
                    }}
                    isOnlyAvailableInStore={isOnlyAvailableInStore}
                    displayStockLevel={displayStockLevel}
                />
            </div>
            <div className="storePicker-layout--footer">
                {!isOnlyAvailableInStore && (
                    <React.Fragment>
                        <p className="d-none d-md-block">
                            {displayStockLevel &&
                                t("general.storepicker.label.pickupTime")}
                            {infoLink && (
                                <a href={infoLink} className={"ml-1"}>
                                    {t("general.storepicker.label.aboutCNC")}
                                </a>
                            )}
                        </p>
                        {!customSubmit && (
                            <Button
                                test="storePicker-button-submit"
                                onClick={onClick}
                                disabled={isDisabled()}
                                type="button"
                                fullWidth
                            >
                                <strong>{t(buttonTk)}</strong>
                            </Button>
                        )}
                        {customSubmit && customSubmit(onClick)}
                    </React.Fragment>
                )}
            </div>
        </div>
    );
};

export default StorePicker;
